import { formatClockParts } from '@/shared/lib/time-format';
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import { ymdLocal } from '@/shared/lib/ymd-local';

/**
 * GET /api/Doctor/Presence
 *
 * Returns the practitioner's working shifts within the requested
 * day range. Used by the BlockAvailability panel to populate the
 * SHIFTS dropdown — selecting a shift bounds the break form's
 * start/end time pickers to that shift's hours.
 *
 * Flutter parity: `block_availability_widget.dart:112-132`
 * (`getPresenceList()` → `PresenceListEvent`).
 *
 * Wire shape verified via dev curl 2026-05-18.
 * Mirrors Flutter `PresenceList` (`presence_list_response.dart:13-66`).
 */
export interface DoctorPresence {
  isReadOnly: boolean | null;
  isAnytime: boolean | null;
  isMoment: boolean | null;
  hasStart: boolean | null;
  /** ISO datetime — shift's start in clinic local time. */
  start: string | null;
  hasEnd: boolean | null;
  /** ISO datetime — shift's end. */
  end: string | null;
  /** Backend-formatted duration string (rarely used). */
  duration: string | null;
  /** Human-readable, e.g. "12 Hours". Renders inline on the SHIFTS option. */
  durationDescription: string | null;
  id: string | null;
}

interface UseDoctorPresenceParams {
  practitionerId: string | null;
  /** Day Date object — hook expands to start/end of day in the query
   *  string, matching Flutter's `getPresenceList` (`:112-132`). */
  date: Date | null;
}

/**
 * Format `2026-05-18` (or any Date) as `YYYY-MM-DD HH:mm:ss.sss`
 * (URL-encoded). Verified against the dev curl:
 *   `?from=2026-05-18 00:00:00.000&to=2026-05-18 23:59:59.000`
 *
 * Built from local-clock components — same defense against UTC
 * day-shift as `composeWireDateTime` in `block-availability-form-state.ts`.
 */
function formatDayBound(date: Date, endOfDay: boolean): string {
  const time = endOfDay ? '23:59:59.000' : '00:00:00.000';
  return `${ymdLocal(date)} ${time}`;
}

export function useDoctorPresence({ practitionerId, date }: UseDoctorPresenceParams) {
  const enabled = !!practitionerId && !!date;
  return useQuery({
    // Key on the LOCAL day so it matches the `from`/`to` the request actually
    // sends (formatDayBound above) rather than a UTC date a day behind it.
    queryKey: ['doctor-presence', practitionerId, date ? ymdLocal(date) : null],
    queryFn: ({ signal }) => {
      if (!practitionerId || !date) {
        throw new Error('useDoctorPresence: practitionerId and date are required');
      }
      const from = encodeURIComponent(formatDayBound(date, false));
      const to = encodeURIComponent(formatDayBound(date, true));
      return get<DoctorPresence[]>(
        `/api/Doctor/Presence?practitionerId=${practitionerId}&from=${from}&to=${to}`,
        { signal },
      );
    },
    enabled,
  });
}

/**
 * Compact shift label for the dropdown. Mirrors Flutter
 * `break_widget.dart:270-279`:
 *   `${scTimeFormatSingleLetter(item.start)}${emptyLabel}${scTimeFormatSingleLetter(item.end)} (${item.durationDescription})`
 * which renders as e.g. `8:00a - 8:00p (12 Hours)`.
 */
export function presenceShiftLabel(presence: DoctorPresence): string {
  const start = presence.start ? compactTime(presence.start) : '';
  const end = presence.end ? compactTime(presence.end) : '';
  const range = start && end ? `${start} - ${end}` : start || end;
  const duration = presence.durationDescription
    ? ` (${presence.durationDescription})`
    : '';
  return `${range}${duration}`.trim();
}

function compactTime(iso: string): string {
  // Backend ships clinic-local wall clock stamped as UTC (`Z` suffix
  // is misleading). Flutter renders "8:00a" for `08:00:00Z` —
  // verified via response sample 2026-05-18. Use UTC getters so the
  // displayed hour matches the wire hour for users in any timezone.
  const d = new Date(iso);
  return formatClockParts(d.getUTCHours(), d.getUTCMinutes());
}

/**
 * Extract the `HH:MM` 24h time-of-day portion from an ISO datetime
 * — the value `<input type="time">` accepts for min/max.
 *
 * UTC getters intentionally — same reason as `compactTime` above:
 * the backend's presence response stamps clinic-local times with `Z`.
 * Reading them as local time would shift the bound by the user's
 * timezone offset.
 */
export function presenceTimeOfDay(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  const h = String(d.getUTCHours()).padStart(2, '0');
  const m = String(d.getUTCMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}
